import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { supabase } from '../../services/supabase';
import { Trash2, RefreshCw } from 'lucide-react';

export const AdminSignals: React.FC = () => {
    const [signals, setSignals] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'active' | 'pending' | 'closed'>('all');

    useEffect(() => { fetchSignals(); }, []);

    const fetchSignals = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('signals')
            .select('id, signal_number, signal_type, strategy_id, scanner_type, symbol, pair, entry_type, limit_entry, entry_price, stop_loss, sl, take_profit, tp2, result, status, rr_ratio, rr_at_tp1, rr_ratio_tp2, be_closed, closed_at, created_at')
            .order('created_at', { ascending: false })
            .limit(100);
        if (data) setSignals(data);
        setLoading(false);
    };

    const deleteSignal = async (id: string) => {
        if (!window.confirm('Delete this signal?')) return;
        const { error } = await supabase.rpc('admin_delete_signal', { signal_id: id });
        if (error) {
            alert(`Delete failed: ${error.message}\n\n⚠️ Run admin_rpc_functions.sql in Supabase SQL Editor first.`);
        } else { fetchSignals(); }
    };

    const filteredSignals = signals.filter(s => {
        if (filter === 'active') return s.result === 'active';
        if (filter === 'pending') return s.result === 'pending';
        if (filter === 'closed') return ['win', 'loss', 'invalid'].includes(s.result);
        return true;
    });

    const getResultBadge = (s: any) => {
        if (s.result === 'win' && s.status === 'be_closed') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-amber-500/20 text-amber-400 border border-amber-500/30">WIN (BE)</span>;
        if (s.result === 'win' && s.status === 'tp1_hit') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-blue-500/20 text-blue-400 border border-blue-500/30">TP1 RUNNING</span>;
        if (s.result === 'win') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">WIN</span>;
        if (s.result === 'loss') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-red-500/20 text-red-400 border border-red-500/30">SL HIT</span>;
        if (s.result === 'pending') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">PENDING</span>;
        if (s.result === 'active') return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-green-500/20 text-green-400 border border-green-500/30 animate-pulse">ACTIVE</span>;
        return <span className="px-2 py-0.5 rounded text-[10px] font-black bg-gray-500/20 text-gray-400">{s.result}</span>;
    };

    const rrColor = (rr: number | null) => {
        if (rr === null || rr === undefined) return 'text-gray-500';
        if (rr > 0) return 'text-emerald-400';
        if (rr < 0) return 'text-rose-400';
        return 'text-amber-400';
    };

    return (
        <AdminLayout>
            <div className="mb-6 flex justify-between items-center flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Signal Management</h1>
                    <p className="text-gray-400 text-sm">Full audit view — status, RR, TP1 locked, BE flags, closed timestamps.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {(['all', 'active', 'pending', 'closed'] as const).map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filter === f ? 'bg-finance-accent text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                            {f}
                        </button>
                    ))}
                    <button onClick={fetchSignals} className="p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 transition-all">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            <div className="bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-gray-400 min-w-[1100px]">
                        <thead className="bg-gray-800/50 text-gray-300 text-[10px] uppercase tracking-widest">
                            <tr>
                                <th className="px-4 py-3 font-black">#</th>
                                <th className="px-4 py-3 font-black">Pair / Type</th>
                                <th className="px-4 py-3 font-black">Strategy</th>
                                <th className="px-4 py-3 font-black">Entry / SL / TP1 / TP2</th>
                                <th className="px-4 py-3 font-black">Status</th>
                                <th className="px-4 py-3 font-black">RR Ratio</th>
                                <th className="px-4 py-3 font-black">TP1 Locked</th>
                                <th className="px-4 py-3 font-black">Closed At</th>
                                <th className="px-4 py-3 font-black">Del</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/60">
                            {loading ? (
                                <tr><td colSpan={9} className="px-6 py-8 text-center">Loading signals...</td></tr>
                            ) : filteredSignals.length === 0 ? (
                                <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-600">No signals found.</td></tr>
                            ) : (
                                filteredSignals.map((sig) => (
                                    <tr key={sig.id} className="hover:bg-gray-800/20 transition-colors">
                                        <td className="px-4 py-3 font-mono text-[10px] text-gray-500">#{sig.signal_number || '—'}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-bold text-white text-xs">{sig.symbol || sig.pair || 'XAUUSD'}</div>
                                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${sig.signal_type === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                                {sig.signal_type} {sig.entry_type && `(${sig.entry_type})`}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-[10px]">{sig.strategy_id}</div>
                                            <div className="text-[9px] text-gray-600">{sig.scanner_type}</div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[10px] space-y-0.5">
                                            <div>E: <span className="text-white">{sig.limit_entry || sig.entry_price || '—'}</span></div>
                                            <div>SL: <span className="text-rose-400">{sig.sl || sig.stop_loss || '—'}</span></div>
                                            <div>TP1: <span className="text-emerald-400">{sig.take_profit || '—'}</span></div>
                                            {sig.tp2 && <div>TP2: <span className="text-blue-400">{sig.tp2}</span></div>}
                                        </td>
                                        <td className="px-4 py-3">{getResultBadge(sig)}</td>
                                        <td className="px-4 py-3">
                                            <div className={`font-black font-mono text-xs ${rrColor(sig.rr_ratio)}`}>
                                                {typeof sig.rr_ratio === 'number' ? `${sig.rr_ratio >= 0 ? '+' : ''}${sig.rr_ratio.toFixed(2)}R` : '—'}
                                            </div>
                                            {sig.rr_ratio_tp2 && <div className="text-[9px] text-blue-400/70 font-mono">TP2: +{sig.rr_ratio_tp2.toFixed(2)}R</div>}
                                            {sig.be_closed && <div className="text-[9px] text-amber-400 font-black">BE CLOSED ✓</div>}
                                        </td>
                                        <td className="px-4 py-3 font-mono">
                                            {typeof sig.rr_at_tp1 === 'number'
                                                ? <span className="text-amber-300 font-black text-xs">+{sig.rr_at_tp1.toFixed(2)}R</span>
                                                : <span className="text-gray-700">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-[10px]">
                                            {sig.closed_at
                                                ? <span className="text-gray-400">{new Date(sig.closed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                : <span className="text-gray-700">Active</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => deleteSignal(sig.id)} className="text-red-400 hover:text-red-300 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
};
